import { useTranslation } from "@sagot/i18n";
import { Badge, Button, CodeBlock, Icon, Textarea } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../lib/api-errors";
import { useBuildFormatters } from "../shared/format";
import type { Action, ActionTestOut } from "../shared/types";

const JSON_INDENT = 2;

function parseInput(text: string): Record<string, unknown> | null {
  try {
    const value: unknown = JSON.parse(text);
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function Result({ result }: { result: ActionTestOut }) {
  const { t } = useTranslation();
  const fmt = useBuildFormatters();
  const status = result.status === null
    ? { tone: "danger" as const, icon: "x-circle" as const, label: t("build.actions.test.failed") }
    : result.ok
      ? { tone: "success" as const, icon: "check-circle" as const, label: t("build.actions.test.ok", { status: result.status }) }
      : { tone: "danger" as const, icon: "alert-circle" as const, label: t("build.actions.test.status", { status: result.status }) };
  return (
    <div className="space-y-3" data-testid="action-test-result">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Badge tone={status.tone}>
          <Icon name={status.icon} className="size-3" />
          {status.label}
        </Badge>
        <span className="font-mono text-fg-muted">{t("build.actions.test.meta", { duration: fmt.duration(result.duration_ms), bytes: fmt.number(result.bytes) })}</span>
      </div>
      {result.errors.length > 0 ? (
        <div className="rounded-md border border-line bg-surface p-3 text-xs">
          <div className="font-semibold">{t("build.actions.test.errors")}</div>
          <ul className="mt-1 list-disc pl-4 font-mono text-fg-muted">
            {result.errors.map((error) => <li key={error}>{error}</li>)}
          </ul>
        </div>
      ) : null}
      {result.body === null || result.body === undefined ? null : (
        <CodeBlock label={t("build.actions.test.response")} code={JSON.stringify(result.body, null, JSON_INDENT)} copyLabel={t("common.copy")} copiedLabel={t("common.copied")} />
      )}
      {result.status === null ? null : (
        <CodeBlock label={t("build.actions.test.receives")} code={JSON.stringify(result.mapped, null, JSON_INDENT)} copyLabel={t("common.copy")} copiedLabel={t("common.copied")} />
      )}
    </div>
  );
}

interface TestPanelProps {
  action: Action | null;
  sample: string;
  canRun: boolean;
  running: boolean;
  result: ActionTestOut | undefined;
  error: unknown;
  onRun: (input: Record<string, unknown>) => void;
}

/** Test console: validated JSON sample → live executor (saved custom actions only). */
export function TestPanel({ action, sample, canRun, running, result, error, onRun }: TestPanelProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const [text, setText] = useState(sample);
  const [invalid, setInvalid] = useState(false);
  const note = !action ? t("build.actions.test.saveFirst") : action.type !== "http" ? t("build.actions.test.builtin") : null;
  const run = () => {
    const input = parseInput(text);
    setInvalid(input === null);
    if (input) onRun(input);
  };
  return (
    <section className="space-y-3 rounded-lg border border-line bg-surface-2 p-4" aria-labelledby="action-test-title">
      <div className="flex items-center justify-between">
        <h4 id="action-test-title" className="flex items-center gap-2 text-sm font-bold">
          <Icon name="flask" className="size-4 text-primary-ink" />
          {t("build.actions.test.title")}
        </h4>
        <span className="text-xs text-fg-subtle">{t("build.actions.test.live")}</span>
      </div>
      {note ? (
        <p className="text-xs text-fg-muted">{note}</p>
      ) : (
        <>
          <label htmlFor="action-test-input" className="label">{t("build.actions.test.input")}</label>
          <Textarea id="action-test-input" rows={5} spellCheck={false} className="code w-full resize-y" value={text} invalid={invalid} onChange={(e) => { setText(e.target.value); }} />
          {invalid ? <p className="text-xs font-semibold text-danger" role="alert">{t("build.actions.test.invalidJson")}</p> : null}
          <Button type="button" variant="dark" onClick={run} loading={running} disabled={!canRun}>
            <Icon name="play" />
            {t("build.actions.test.run")}
          </Button>
          {error ? <p className="text-xs font-semibold text-danger" role="alert">{message(error)}</p> : null}
          <div aria-live="polite">{result ? <Result result={result} /> : null}</div>
        </>
      )}
    </section>
  );
}
